import { max } from 'd3';

import Axis from '../Base/axis';
import Data from '../Base/data';
import Dims from '../Base/dims';
import Margin from '../Base/margin';
import Svg from '../Base/svg';
import Transforms from '../Base/transforms';
import ToolTip from '../ToolTip';
import Utils from '../Utils';

import { ChartCtrParams } from '../types';

/**
 * Lollipop chart class.
 * Generally used to show the relationship between a numerical and categorical variable.
 */
export default class Lolli {
  private elementId: string;
  private element: Svg;

  private dims: Dims;
  private margin: Margin;

  private toolTip: ToolTip;
  private useToolTip = false;
  private animators: any;

  public data: Data;
  private _xField = 'x';
  private _yField = 'y';

  public xAxis: Axis;
  public yAxis: Axis;

  public transforms: Transforms;
  private pointSize = 7; // TODO: Should be changeable

  /**
   * Constructs a new LolliChart instance
   * ```ts
   * const data = [{ x: 'US', y: 12394 }, ... ]
   * const line = new AchoViz.LolliChart({ elementId: '#lolli', data });
   * ```
   * @param params - constructor params
   * @returns instance of a LolliChart
   */
  constructor(params: ChartCtrParams = {}) {
    this.elementId = params.elementId ?? '';

    this.dims = new Dims(params.height, params.width);
    this.margin = new Margin(params.margin);
    this.data = new Data(params.data);

    this._xField = params.xField ?? 'x';
    this._yField = params.yField ?? 'y';

    const xTransform = params.xTransform ?? 'band';
    const yTransform = params.yTransform ?? 'linear';

    this.transforms = new Transforms({ [this._xField]: xTransform, [this._yField]: yTransform });

    this.xAxis = new Axis({ loc: 'bottom' });
    this.yAxis = new Axis({ loc: 'left' });
    this._useAutoAxis();

    this.useToolTip = params.useToolTip ?? false;
    // TODO: Configure tool tip
  }

  /**
   * Renders, draws, and displays the chart on the dom
   */
  public render() {
    const { elementId, margin } = this;
    const { height, width } = this.dims.both();

    this.element = new Svg({ elementId, height, width, margin });
    this.element.draw();

    this._drawXAxis();
    this._drawYAxis();
    this._drawLines();
    this._drawCircles();
  }

  /**
   * Draws the instance's x axis
   */
  private _drawXAxis() {
    const { height: h } = this.dims.marDims(this.margin);
    const translate = `translate(0, ${h})`;
    const ax = Utils.drawAxis(this.element.d3Svg, this.xAxis, { translate });

    // TODO: axis ticks config/user customizability
    ax.selectAll('text').style('display', 'none');
  }

  /**
   * Draws the instance's y axis
   */
  private _drawYAxis() {
    const { d3Svg } = this.element;
    Utils.drawAxis(d3Svg, this.yAxis);
  }

  /**
   * Draws the lines that bind to the data provided
   */
  private _drawLines() {
    const { xAxis, yAxis, _xField, _yField } = this;
    const data = this.data.consume();
    const lines = this.element.d3Svg.selectAll('lines').data(data).enter();

    const attr = {
      x1: function (d) {
        return xAxis.scale(d[_xField]);
      },
      x2: function (d) {
        return xAxis.scale(d[_xField]);
      },
      y1: function (d) {
        return yAxis.scale(d[_yField]);
      },
      y2: yAxis.scale(0),
      stroke: '#A6CEE3',
    };

    Utils.appendToSelection(lines, 'line', { attr });
  }

  /**
   * Draws the circles on the lines that bind to the data provided
   */
  private _drawCircles() {
    const { xAxis, yAxis, _xField, _yField } = this;
    const data = this.data.consume();
    const circles = this.element.d3Svg.selectAll('circles').data(data).enter();
    const attr = {
      cx: function (d) {
        return xAxis.scale(d[_xField]);
      },
      cy: function (d) {
        return yAxis.scale(d[_yField]);
      },
      r: this.pointSize,
      stroke: '#A6CEE3',
    };
    const styles = {
      fill: 'steelblue',
    };
    Utils.appendToSelection(circles, 'circle', { attr, styles });
  }

  /**
   * Hook that automatically configures the axis' domains, ranges, and scales
   */
  private _useAutoAxis() {
    const { height, width } = this.dims.marDims(this.margin);
    const data = this.data.consume();

    const xDomain = this.data.consumeByField(this._xField);
    const xRange = [0, width];
    const xScaleFn = this.transforms.getScale(this._xField);
    const xScale = xScaleFn().domain(xDomain).range(xRange).padding(1);

    this.xAxis.setDomain(xDomain);
    this.xAxis.setRange(xRange);
    this.xAxis.setScale(xScale);

    const yMax = max(data, (d) => d[this._yField]);
    const yDomain = [0, yMax];
    const yRange = [height, 0];
    const yScaleFn = this.transforms.getScale(this._yField);
    const yScale = yScaleFn().domain(yDomain).range(yRange);

    this.yAxis.setDomain(yDomain);
    this.yAxis.setRange(yRange);
    this.yAxis.setScale(yScale);
  }
}
